import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SparklesIcon, XIcon, SendIcon, RotateCcwIcon } from "lucide-react";
import { useAskOceanside } from "../../hooks/useAskOceanside";
import { assistantStarters } from "../../data/assistantScript";

// Post-conversion enhancement in Bricks. Prototype: scripted, clearly labelled AI demo. Never auto-opens.
export function AskOceanside() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const {
    messages,
    ask,
    choose,
    reset
  } = useAskOceanside();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight
    });
  }, [messages]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    ask(draft);
    setDraft("");
  };
  return <>
      {!open && <button ref={launcherRef} type="button" onClick={() => setOpen(true)} className="fixed bottom-4 right-4 z-30 inline-flex h-12 items-center gap-2 rounded-full border border-charcoal/10 bg-charcoal px-4 text-[15px] font-semibold text-paper shadow-[0_6px_20px_rgba(43,39,39,0.18)] transition-colors duration-150 ease-out hover:bg-[#3a3535] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean focus-visible:ring-offset-2 max-md:bottom-[88px] md:bottom-6 md:right-6" data-id="element-388" data-mp="3utg5nhnqwhkt">
          <SparklesIcon className="h-4 w-4 text-ocean" aria-hidden="true" data-id="element-389" data-mp="kyle05rk68ld" />
          Ask Oceanside
          <span className="sr-only" data-id="element-390" data-mp="g0a0nl221fye">: open AI assistant demonstration</span>
        </button>}

      <AnimatePresence data-id="element-391" data-mp="b7gviw2oc5ls">
        {open && <motion.div role="dialog" aria-modal="false" aria-labelledby="assistant-title" initial={reduceMotion ? false : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col border border-line bg-paper shadow-[0_12px_40px_rgba(43,39,39,0.2)] md:inset-x-auto md:bottom-6 md:right-6 md:h-[600px] md:max-h-[calc(100vh-120px)] md:w-[400px] md:rounded-[4px]" data-id="element-392" data-mp="b6fn6zd1lx7r">
            <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4" data-id="element-393" data-mp="1hpdqktfa9iie">
              <div data-id="element-394" data-mp="1p7ttlmsqstpu">
                <h2 id="assistant-title" className="flex items-center gap-2 text-lg font-semibold" data-id="element-395" data-mp="1p695og0xo90y">
                  <SparklesIcon className="h-4 w-4 text-slate" aria-hidden="true" data-id="element-396" data-mp="1yxwusaa0pdz9" />
                  Ask Oceanside
                </h2>
                <p className="mt-0.5 text-[13px] text-slate" data-id="element-397" data-mp="23x9j6zgjet3y">AI assistant · scripted demonstration, not a live model or staff member</p>
              </div>
              <div className="flex items-center" data-id="element-398" data-mp="16blwx7dspr65">
                <button type="button" onClick={reset} className="inline-flex h-10 w-10 items-center justify-center rounded-[4px] hover:bg-paper-deep" aria-label="Restart conversation" data-id="element-399" data-mp="1b9dlbrf8ev5l">
                  <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-400" data-mp="1l6s25s52hlyf" />
                </button>
                <button type="button" onClick={() => {
              setOpen(false);
              requestAnimationFrame(() => launcherRef.current?.focus());
            }} className="inline-flex h-10 w-10 items-center justify-center rounded-[4px] hover:bg-paper-deep" aria-label="Close assistant" data-id="element-401" data-mp="2rndk2z2ccer9">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-402" data-mp="3467sf2q0olt1" />
                </button>
              </div>
            </div>

            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" aria-live="polite" data-id="element-403" data-mp="2z4ii5pamcc60">
              {messages.map(message => <div key={message.id} className={message.from === "user" ? "flex justify-end" : ""} data-id="element-404" data-mp="394su76kx3djl">
                  <div className={message.from === "user" ? "max-w-[85%] rounded-[4px] bg-charcoal px-4 py-2.5 text-[15px] text-paper" : "max-w-[92%] border-l-2 border-ocean bg-white px-4 py-3 text-[15px] leading-relaxed text-charcoal"} data-id="element-405" data-mp="3mw7bn8z6f6oj">
                    <p data-id="element-406" data-mp="3rsxta0qmqac2">{message.text}</p>
                    {message.links && <ul className="mt-3 space-y-1.5" data-id="element-407" data-mp="fby4qllj6ugq">
                        {message.links.map(link => <li key={link.to} data-id="element-408" data-mp="jaf94xdkhqug">
                            <Link to={link.to} onClick={() => setOpen(false)} className="font-medium underline underline-offset-4" data-id="element-409" data-mp="5bxf6hx2ji43">
                              {link.label}
                            </Link>
                          </li>)}
                      </ul>}
                  </div>
                </div>)}
            </div>

            <div className="border-t border-line px-5 pb-5 pt-4" data-id="element-410" data-mp="3isocp60obhl2">
              <div className="flex flex-wrap gap-2" data-id="element-411" data-mp="3nqzlrej59o28">
                {assistantStarters.map(starter => <button key={starter.id} type="button" onClick={() => choose(starter.id, starter.label)} className="rounded-[4px] border border-line bg-white px-3 py-2 text-sm font-medium text-charcoal transition-colors duration-150 ease-out hover:border-charcoal" data-id="element-412" data-mp="38tz5q8abfhjl">
                    {starter.label}
                  </button>)}
              </div>
              <form onSubmit={submit} className="mt-3 flex gap-2" data-id="element-413" data-mp="2kw9kdpc0dz2j">
                <label htmlFor="assistant-input" className="sr-only" data-id="element-414" data-mp="2g0c75rw1l8tz">Ask a question</label>
                <input ref={inputRef} id="assistant-input" value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask about designs or the process" className="min-w-0 flex-1 rounded-[4px] border border-line bg-white px-3 py-2.5 text-base focus:border-charcoal focus:outline-none focus:ring-2 focus:ring-ocean" data-id="element-415" data-mp="3dm00g3x7bb1a" />
                <button type="submit" className="inline-flex h-11 w-11 items-center justify-center rounded-[4px] bg-ocean text-charcoal hover:bg-ocean-light" aria-label="Send question" data-id="element-416" data-mp="1nalnl3bn5eqh">
                  <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-417" data-mp="2qhzq0z3fejf" />
                </button>
              </form>
              <p className="mt-2 text-xs text-slate" data-id="element-418" data-mp="cqqwph0wt3zv">Please don’t share personal details here. Nothing you type is sent anywhere.</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}